// contexts/SyncContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import {
  getQueueStats, processPendingQueue, cleanupSynced, requestBackgroundSync,
  resetBackoff, saveUploadSettingsForWorker, handOverToNative,
} from '../utils/offlineQueue';
import {
  hasNativeUploader, nativeGetStats, nativeSyncNow, nativeGetAppInfo, nativeIsReliable, nativeEnqueuePhoto,
} from '../utils/nativeBridge';
import {
  loadUploadSettings, storeUploadSettings, pickUploadSettings, syncWindowState, isUploadWindowOpen,
  msUntilWindowOpens,
} from '../utils/syncWindow';
import { useAuth } from './AuthContext';

const SyncContext = createContext(null);

const WARMUP_STORAGE_KEY = 'photoapp_last_warmup';
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
const INSTALL_ID_KEY = 'photoapp_install_id';
const SITE_SETTINGS_INTERVAL_MS = 10 * 60 * 1000;
// "Sync now" stays visibly busy at least this long, and its result stays on
// screen long enough to read, so a tap never looks like it did nothing.
const MIN_BUSY_MS = 800;
const RESULT_VISIBLE_MS = 10000;
const NATIVE_DRAIN_POLLS = 8;
const NATIVE_DRAIN_POLL_MS = 1000;
const NATIVE_STALLED_POLLS = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const queued = (stats) => (stats ? (stats.pending || 0) + (stats.failed || 0) : 0);

// The native queue uploads on its own thread and reports no result, so watch
// its counters for a few seconds to tell the user how many photos went out.
// Stops early once the queue is empty or no longer moving (no connection).
async function waitForNativeDrain(before) {
  let latest = before;
  let stalled = 0;
  for (let i = 0; i < NATIVE_DRAIN_POLLS && queued(latest) > 0 && stalled < NATIVE_STALLED_POLLS; i++) {
    await sleep(NATIVE_DRAIN_POLL_MS);
    const next = (await nativeGetStats()) || latest;
    stalled = queued(next) === queued(latest) ? stalled + 1 : 0;
    latest = next;
  }
  return latest;
}

// Tablet shell (APK 2.1+) on a site with a sync window: photos taken outside
// the window wait in the web queue, because the installed APK uploads whatever
// it gets straight away. Once they may go (window open, Sync now, or held for
// more than a day) they move to the native uploader, which carries on with the
// app closed. Without a window the web queue is left exactly as it was.
async function nativeTakesHeldPhotos() {
  if (!Number(loadUploadSettings()?.sync_window_enabled)) return false;
  return hasNativeUploader() && !!(await nativeIsReliable());
}

async function handOverHeldPhotos(forced) {
  try {
    if (!(await nativeTakesHeldPhotos())) return 0;
    return (await handOverToNative(nativeEnqueuePhoto, { windowOpen: forced || isUploadWindowOpen() })) || 0;
  } catch {
    return 0; // The web queue still holds the photos and uploads them itself.
  }
}

// Stabiel id per tablet-installatie. De capture-code gebruikt de user-agent als
// device_id, maar die is gelijk voor identieke tablets en dus niet uniek genoeg.
function getInstallId() {
  try {
    let id = localStorage.getItem(INSTALL_ID_KEY);
    if (!id) {
      id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(INSTALL_ID_KEY, id);
    }
    return id;
  } catch {
    return navigator.userAgent.slice(0, 40);
  }
}

export function SyncProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState({ pending: 0, failed: 0, total: 0 });
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const [isWarming, setIsWarming] = useState(false);
  const [lastWarmup, setLastWarmup] = useState(() => {
    const ts = localStorage.getItem(WARMUP_STORAGE_KEY);
    return ts ? new Date(ts) : null;
  });
  const [keepAwake, setKeepAwake] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [uploadSettings, setUploadSettings] = useState(loadUploadSettings);
  const [windowTick, setWindowTick] = useState(0);
  const { user } = useAuth();
  const syncLockRef = useRef(false);
  const syncRunRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastHeartbeatRef = useRef(0);

  const refreshStats = useCallback(async () => {
    try {
      const localStats = await getQueueStats();
      // In the Capacitor shell photos live in the native WorkManager queue,
      // not IndexedDB — count those too so the sync bar tells the truth.
      const native = hasNativeUploader() ? await nativeGetStats() : null;
      // Meld de wachtrij aan de server (max eens per 2 min, alleen ingelogd en online).
      if (isOnline && localStorage.getItem('auth_token') && Date.now() - lastHeartbeatRef.current >= HEARTBEAT_INTERVAL_MS) {
        lastHeartbeatRef.current = Date.now();
        (async () => {
          try {
            const info = hasNativeUploader() ? await nativeGetAppInfo() : null;
            await api.post('/api/sync/heartbeat', {
              device_id: getInstallId(),
              native_pending: native?.pending || 0,
              native_failed: native?.failed || 0,
              local_pending: localStats.pending || 0,
              local_failed: localStats.failed || 0,
              app_version: info?.versionCode ?? null,
            });
          } catch {}
        })();
      }
      let serverPending = 0;
      if (isOnline) {
        try {
          const { data } = await api.get('/api/sync/status');
          serverPending = data.pending || 0;
        } catch {}
      }
      setSyncStatus({
        pending: localStats.pending + (native?.pending || 0) + serverPending,
        failed: localStats.failed + (native?.failed || 0),
        total: localStats.total + (native?.pending || 0) + (native?.failed || 0),
        localPending: localStats.pending,
        localFailed: localStats.failed,
        serverPending,
        bytes: localStats.bytes || 0,
      });
    } catch {}
  }, [isOnline]);

  // One sync round. forced = the "Sync now" button: ignores the sync window and
  // the retry backoff, and always ends in a visible result or error.
  const runSync = useCallback(async (forced) => {
    const startedAt = Date.now();
    try {
      const nativeBefore = forced && hasNativeUploader() ? await nativeGetStats() : null;
      if (forced) await resetBackoff();
      if (hasNativeUploader()) nativeSyncNow();
      // Before the web round, so a photo that moved to the native queue is not
      // also uploaded from here.
      const handedOver = await handOverHeldPhotos(forced);
      const result = await processPendingQueue(api, (progress) => setSyncProgress(progress), {
        force: forced,
        windowOpen: forced || isUploadWindowOpen(),
      });
      setLastSync(new Date());
      if (forced) {
        const nativeStart = handedOver > 0
          ? { pending: (nativeBefore?.pending || 0) + handedOver, failed: nativeBefore?.failed || 0 }
          : nativeBefore;
        const nativeAfter = await waitForNativeDrain(nativeStart);
        await sleep(Math.max(0, MIN_BUSY_MS - (Date.now() - startedAt)));
        const local = await getQueueStats();
        const sent = result.uploaded + Math.max(0, queued(nativeStart) - queued(nativeAfter));
        const left = local.pending + local.failed + queued(nativeAfter);
        setSyncResult({ sent, left, error: sent === 0 && left > 0 ? (result.lastError || 'no-connection') : null });
      }
      await refreshStats();
      const retentionDays = parseInt(localStorage.getItem('local_retention_days') || '7');
      if (retentionDays > 0) await cleanupSynced(retentionDays);
      return result;
    } catch (err) {
      if (!forced) throw err;
      setSyncResult({ sent: 0, left: null, error: err?.message || String(err) });
      return { uploaded: 0, failed: 0, held: 0, lastError: err?.message || String(err) };
    }
  }, [refreshStats]);

  // force=true (the "Sync now" button) skips per-item retry backoff and the
  // sync window. Guard against being used directly as an event handler
  // (event ≠ force). An automatic round is skipped while offline or while
  // another round runs; a forced one never is: navigator.onLine is unreliable
  // in the tablet WebView, and a tap during a slow background round used to be
  // dropped without a trace. It now waits its turn and then runs.
  const syncNow = useCallback(async (force = false) => {
    const forced = force === true;
    if (!forced && syncLockRef.current) return;
    if (!forced && !isOnline) {
      // No upload round offline, but the native uploader waits for the
      // connection by itself, so held photos can already move over.
      await handOverHeldPhotos(false);
      return;
    }
    if (forced) {
      setIsSyncing(true);
      setSyncResult(null);
    }
    while (syncLockRef.current && syncRunRef.current) {
      await syncRunRef.current.catch(() => {});
    }
    syncLockRef.current = true;
    setIsSyncing(true);
    setSyncProgress(null);
    const run = runSync(forced);
    syncRunRef.current = run;
    try {
      return await run;
    } finally {
      syncLockRef.current = false;
      syncRunRef.current = null;
      setIsSyncing(false);
      setSyncProgress(null);
    }
  }, [isOnline, runSync]);

  useEffect(() => {
    if (!syncResult) return undefined;
    const timer = setTimeout(() => setSyncResult(null), RESULT_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [syncResult]);

  // Upload settings of this user's own site (sync window, photo size). They
  // arrive with the site data; the last known copy is kept in localStorage and
  // mirrored for the service worker, so the window also holds offline. An
  // account without a site (admin) has no window.
  const siteId = user?.site_id || null;
  const loggedIn = !!user;
  useEffect(() => {
    if (!loggedIn) return undefined;
    let cancelled = false;
    const apply = (settings) => {
      if (cancelled) return;
      storeUploadSettings(settings);
      // The worker learns whether the native uploader takes the held photos,
      // so it does not upload the same files alongside it.
      nativeTakesHeldPhotos().catch(() => false).then((native) => {
        saveUploadSettingsForWorker(settings && native ? { ...settings, native_handover: 1 } : settings);
      });
      setUploadSettings(settings);
    };
    const refresh = async () => {
      if (!siteId) return apply(null);
      try {
        const { data } = await api.get('/api/sites');
        const site = Array.isArray(data) ? data.find((s) => s.id === siteId) : null;
        if (site) apply(pickUploadSettings(site));
      } catch {
        // Offline or server down: keep working on the last known settings.
      }
    };
    refresh();
    const interval = setInterval(refresh, SITE_SETTINGS_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [loggedIn, siteId, isOnline]);

  // The moment the window opens, held photos start by themselves: a timer set
  // for the next opening runs a round (which also hands them to the native
  // uploader) and arms the background sync for when the app gets closed. A
  // page in the background can have its timers frozen, so coming back to the
  // foreground checks again. After each opening the timer is set for the next.
  useEffect(() => {
    const kick = () => {
      setWindowTick((n) => n + 1);
      syncNow();
      requestBackgroundSync();
    };
    const untilOpen = msUntilWindowOpens(uploadSettings, new Date());
    if (untilOpen === null) return undefined; // no window: nothing changes
    const timer = setTimeout(kick, untilOpen + 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') kick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uploadSettings, syncNow, windowTick]);

  // Recomputed on every render; refreshStats re-renders at least every 30s.
  const windowState = syncWindowState(uploadSettings, new Date());
  const uploadWindow = {
    enabled: !!Number(uploadSettings?.sync_window_enabled),
    open: windowState.open,
    opensAt: uploadSettings?.sync_window_start || null,
    waiting: (syncStatus.localPending || 0) + (syncStatus.localFailed || 0),
  };

  // Warm the API cache so reference data (sites, orders, checklists) is available
  // when the tablet later disconnects. Triggered automatically on login + visit-home.
  const warmOfflineCache = useCallback(async () => {
    if (!isOnline) return false;
    setIsWarming(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'warm-api-cache', token });
      } else {
        // Fall back to plain fetches so the SW picks them up as it caches network responses.
        await Promise.allSettled([
          api.get('/api/auth/me'),
          api.get('/api/sites'),
          api.get('/api/orders/mine'),
          api.get('/api/orders/recent'),
          api.get('/api/checklists').catch(() => null),
        ]);
      }
      const now = new Date();
      localStorage.setItem(WARMUP_STORAGE_KEY, now.toISOString());
      setLastWarmup(now);
      return true;
    } finally {
      setIsWarming(false);
    }
  }, [isOnline]);

  // Connectivity events + SW message bridge.
  useEffect(() => {
    const online = () => {
      setIsOnline(true);
      setTimeout(syncNow, 1000);
      requestBackgroundSync();
    };
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);

    const handleSwMessage = (event) => {
      const data = event.data || {};
      if (data.type === 'request-auth-token') {
        const token = localStorage.getItem('auth_token');
        event.ports[0]?.postMessage({ token });
      }
      if (data.type === 'sync-complete' || data.type === 'photo-synced') {
        setLastSync(new Date());
        refreshStats();
      }
      if (data.type === 'warmup-complete') {
        const now = new Date();
        localStorage.setItem(WARMUP_STORAGE_KEY, now.toISOString());
        setLastWarmup(now);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);

    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
  }, [syncNow, refreshStats]);

  useEffect(() => {
    refreshStats();
    const interval = setInterval(refreshStats, 30000);
    return () => clearInterval(interval);
  }, [refreshStats]);

  // Screen wake lock: while photos still sit in the LOCAL browser queue, keep
  // the screen on so a tablet left on its charger overnight keeps uploading
  // (Android freezes the app as soon as the screen sleeps). Released the moment
  // the queue drains. The browser drops the lock whenever the page is hidden,
  // so re-acquire on visibilitychange. The native shell (APK v2+) uploads via
  // WorkManager with the screen off, so its queue never lands here.
  const hasLocalQueue = (syncStatus.localPending || 0) + (syncStatus.localFailed || 0) > 0;
  useEffect(() => {
    if (!('wakeLock' in navigator)) return;
    let cancelled = false;

    async function acquire() {
      if (wakeLockRef.current || document.visibilityState !== 'visible') return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) { lock.release().catch(() => {}); return; }
        wakeLockRef.current = lock;
        setKeepAwake(true);
        lock.addEventListener('release', () => {
          wakeLockRef.current = null;
          setKeepAwake(false);
        });
      } catch {
        // Battery saver or hidden page: not critical, the 60s retry timer
        // still runs whenever the app is actually in the foreground.
      }
    }
    function release() {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      setKeepAwake(false);
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && hasLocalQueue) acquire();
    };
    if (hasLocalQueue) acquire(); else release();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hasLocalQueue]);

  // Periodic retry while the app is open: on a flaky-but-"online" connection
  // the 'online' event never fires, so without this timer dropped uploads
  // would only retry on the next app launch. processPendingQueue respects
  // per-item backoff, so this stays cheap when nothing is due.
  useEffect(() => {
    const interval = setInterval(() => {
      if (navigator.onLine) syncNow();
    }, 60000);
    return () => clearInterval(interval);
  }, [syncNow]);

  return (
    <SyncContext.Provider
      value={{
        isOnline,
        syncStatus,
        isSyncing,
        lastSync,
        syncProgress,
        isWarming,
        lastWarmup,
        keepAwake,
        syncResult,
        uploadWindow,
        uploadSettings,
        syncNow,
        warmOfflineCache,
        refreshStats,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() { return useContext(SyncContext); }
